/**
 * `deriveResolutionWait` — the attacker/source-controller waiting-state, **derived from the
 * server's own verdict rather than guessed at click time**. The live `game_decision` map
 * (`useGameSession().decisions`) is the truthful, per-client signal that a resolution has genuinely
 * halted, who is deciding, and their real display name — covering NPC and mech targets identically.
 *
 * A client-side prediction here cannot be made correct: gating on the target's kind is blind to an
 * NPC target (no built-in reaction, yet a THIRD-PARTY reaction — Valiant Aid, an authored offer —
 * can still halt the resolution) and names the target rather than the actual decider when a third
 * party reacts against a mech.
 *
 * Pure and side-effect-free so it's unit-testable without a DOM (`resolutionWait.test.ts`).
 */
import { controlsCombatant, type EncounterState } from "@lancer-vtt/domain";
import { isMoveCheckpointPromptId } from "@lancer-vtt/rules";
import type { GameDecisionMessage } from "@lancer-vtt/shared";

/** The derived waiting affordance: which live decision to show a banner for, and the decider's name. */
export type ResolutionWait = {
  /** The halted decision's `promptId` — the render key (a repeat wait on the same prompt re-keys). */
  promptId: string;
  /** The decider's display name (`decision.names[decision.who]`) — never the target/enemy. */
  deciderName: string;
  /**
   * Count of matching outstanding decisions (the same filter set below) — always >= 1 whenever the
   * result is non-null, since the newest match itself counts. `ReactionWaitBanner` renders its
   * "+N more" queue-depth line only when this is > 1.
   */
  outstanding: number;
};

/**
 * Derive the waiting affordance for `myActorId`/`myRole` from the live `decisions` map. Takes a
 * single options object (`CODE_STYLE.md`'s max-params-3 gate; mirrors {@link predictsMoveHalt}'s
 * own shape):
 *
 * - Only decisions whose `source` resolves to a combatant THIS client controls — a player
 *   matches `controllerSlotId === myActorId`; a GM additionally matches a **GM-owned** source
 *   (`controllerSlotId` `undefined`/`null`, the type's own "left null for a GM-owned combatant"
 *   contract, `packages/domain/src/encounter/types.ts`) — "my [or my NPC's] action is the one
 *   halted." This deliberately does NOT fall back to blanket `isGm(role) ⇒ true` the way
 *   `canIssueEncounter`'s permission gate does: that gate answers "may the GM command this,"
 *   this answers "is this MY halted action" — a GM does not want a personal waiting banner for
 *   every player's halted attack, only for its own GM-owned units. A decision with no `source`
 *   (no meaningful acting source) never matches.
 * - Excludes a move-checkpoint decision (`isMoveCheckpointPromptId(promptId)`) — a halted move
 *   already has its own surfaces (`TablePausedBanner` + the mover's own `MoveHoldBanner`); no
 *   double banner. For the same reason it excludes any decision whose source is `pausedMoverId`,
 *   the mover the table is paused on: a halt later in the move (a watcher's Rapid Response, a
 *   hazard's System Trauma pick) carries no checkpoint prompt id but holds the same move.
 * - Excludes a decision THIS client itself decides (`whoSlotIds` contains `myActorId`, or
 *   `whoSlotIds === undefined` while `myRole === "gm"`, mirroring the GM-unowned-decider
 *   fallback `RemoteEncounterController.handle`'s `encounter_prompt` branch uses) — the prompt
 *   window/modal supersedes the banner. A GM who is merely a BACKSTOP recipient (`whoSlotIds`
 *   names only other slots) still keeps the banner, so the reaction chain stays legible.
 * - Newest matching entry wins (the map's insertion order — a fresher halt supersedes an older
 *   one this client is also the source-controller for) for `promptId`/`deciderName`; `outstanding`
 *   is the total COUNT of matching entries, computed over the same single pass — not just the
 *   newest one.
 */
export function deriveResolutionWait(args: {
  decisions: Record<string, GameDecisionMessage>;
  state: EncounterState;
  myActorId: string | null;
  myRole: "gm" | "player" | null;
  /** The mover a held move pauses the table on (`encounter_move_paused_event`), or null. */
  pausedMoverId?: string | null;
}): ResolutionWait | null {
  const { decisions, state, myActorId, myRole, pausedMoverId } = args;
  let latest: { promptId: string; deciderName: string } | null = null;
  let outstanding = 0;
  for (const decision of Object.values(decisions)) {
    if (decision.source === undefined) continue;
    const source = state.combatants[decision.source];
    const sourceSlot = source?.controllerSlotId;
    // Two separate questions, deliberately not collapsed: the slot-membership half goes
    // through the one shared `controlsCombatant` predicate, and the GM-owns-an-unowned-source
    // half stays stated here because it is this surface's own rule, not a fact about control
    // (see the predicate's doc).
    const iControlSource =
      controlsCombatant(source, myActorId) ||
      (source !== undefined && (sourceSlot === undefined || sourceSlot === null) && myRole === "gm");
    if (!iControlSource) continue;
    if (isMoveCheckpointPromptId(decision.promptId) || decision.source === pausedMoverId) continue;
    // Membership, not equality: under shared control several slots decide, and each of them
    // gets the modal rather than the banner.
    const weDecide =
      (myActorId !== null && (decision.whoSlotIds?.includes(myActorId) ?? false)) ||
      (decision.whoSlotIds === undefined && myRole === "gm");
    if (weDecide) continue;
    outstanding += 1;
    latest = { promptId: decision.promptId, deciderName: decision.names[decision.who] ?? decision.who };
  }
  return latest === null ? null : { ...latest, outstanding };
}
